import { Message } from "../types/MessageListType";

/** Soft cap for in-memory chat window (excludes date dividers). */
export const MESSAGE_WINDOW_MAX = 280;

/** Height of the pinned-message bar overlay */
export const PIN_BAR_HEIGHT = 52;

const TOMBSTONE_TEXTS = new Set([
  "This message was deleted.",
  "🚫 This message was deleted!",
  "🚫 You deleted this message!",
]);

export function isHiddenDeletedMessage(
  msg: Message | undefined | null,
): boolean {
  if (!msg) return false;
  if (msg.deleted_for_everyone) return true;
  const c = String(msg.message_content || "").trim();
  return TOMBSTONE_TEXTS.has(c);
}

export function withoutHiddenDeleted(records: Message[]): Message[] {
  return records.filter((msg) => !isHiddenDeletedMessage(msg));
}

export function isRealMessage(msg: Message | undefined | null): boolean {
  return (
    !!msg &&
    msg.message_type !== "date" &&
    typeof msg.message_id === "number" &&
    !isHiddenDeletedMessage(msg)
  );
}

export function getOldestMessageId(records: Message[]): number | null {
  for (const msg of records) {
    if (isRealMessage(msg)) return msg.message_id;
  }
  return null;
}

export function getNewestMessageId(records: Message[]): number | null {
  for (let i = records.length - 1; i >= 0; i--) {
    if (isRealMessage(records[i])) return records[i].message_id;
  }
  return null;
}

export function countRealMessages(records: Message[]): number {
  return records.reduce((n, msg) => (isRealMessage(msg) ? n + 1 : n), 0);
}

/**
 * Visual viewport anchor — keep the on-screen bubble fixed across prepend.
 * All corrections run synchronously before paint (useLayoutEffect /
 * ResizeObserver). Never rAF — that paints one wrong frame = flicker.
 */
type VisualAnchor = {
  messageId: string;
  offsetFromContainerTop: number;
};

let pendingAnchor: VisualAnchor | null = null;
let guardCleanup: (() => void) | null = null;
let guardGeneration = 0;
let frozenOverflowY: string | null = null;
let freezeSafetyTimer: number | null = null;

function findVisibleAnchor(container: HTMLElement): HTMLElement | null {
  const nodes = container.querySelectorAll<HTMLElement>("[data-message-id]");
  if (!nodes.length) return null;

  const cRect = container.getBoundingClientRect();
  const targetY = cRect.top + cRect.height * 0.35;
  let best: HTMLElement | null = null;
  let bestDist = Infinity;

  nodes.forEach((node) => {
    const r = node.getBoundingClientRect();
    if (r.bottom < cRect.top || r.top > cRect.bottom) return;
    const dist = Math.abs(r.top + r.height * 0.2 - targetY);
    if (dist < bestDist) {
      bestDist = dist;
      best = node;
    }
  });

  return best;
}

function readAnchor(container: HTMLElement): VisualAnchor | null {
  const el = findVisibleAnchor(container);
  if (!el) return null;
  const id = el.getAttribute("data-message-id") || el.id;
  if (!id) return null;
  const cRect = container.getBoundingClientRect();
  const eRect = el.getBoundingClientRect();
  return {
    messageId: id,
    offsetFromContainerTop: eRect.top - cRect.top,
  };
}

function applyAnchor(container: HTMLElement, snap: VisualAnchor): boolean {
  const el =
    container.querySelector<HTMLElement>(
      `[data-message-id="${snap.messageId}"]`,
    ) || document.getElementById(snap.messageId);
  if (!el) return false;

  const cRect = container.getBoundingClientRect();
  const eRect = el.getBoundingClientRect();
  const currentOffset = eRect.top - cRect.top;
  const delta = currentOffset - snap.offsetFromContainerTop;
  // Ignore sub-pixel noise — it reads as micro-jitter
  if (Math.abs(delta) < 2) return false;
  container.scrollBy({ top: delta, behavior: "instant" });
  return true;
}

function freezeScroll(container: HTMLElement) {
  if (frozenOverflowY != null) return;
  frozenOverflowY = container.style.overflowY;
  container.style.overflowY = "hidden";
  // Safety: never leave the chat stuck non-scrollable if restore is skipped
  // (e.g. prependOlderMessages adds 0 rows → Records.length unchanged).
  if (freezeSafetyTimer != null) window.clearTimeout(freezeSafetyTimer);
  freezeSafetyTimer = window.setTimeout(() => {
    freezeSafetyTimer = null;
    unfreezeScroll(container);
  }, 1200);
}

function unfreezeScroll(container: HTMLElement) {
  if (freezeSafetyTimer != null) {
    window.clearTimeout(freezeSafetyTimer);
    freezeSafetyTimer = null;
  }
  if (frozenOverflowY == null) return;
  container.style.overflowY = frozenOverflowY || "";
  // Ensure Tailwind overflow-y-auto still wins if inline was empty
  if (!container.style.overflowY) {
    container.style.removeProperty("overflow-y");
  }
  frozenOverflowY = null;
}

/** Call immediately before dispatching prependOlderMessages. */
export function captureMessageListScroll(el?: HTMLElement | null) {
  const node = el || document.getElementById("messageListDiv");
  guardCleanup?.();
  guardCleanup = null;
  if (!node) {
    pendingAnchor = null;
    return;
  }
  pendingAnchor = readAnchor(node);
  // Hide browser auto-scroll during the React commit that prepends nodes
  freezeScroll(node);
}

/** useLayoutEffect after Records length changes — before paint. */
export function restoreMessageListScroll(el?: HTMLElement | null) {
  const node = el || document.getElementById("messageListDiv");
  if (!node) return;
  if (pendingAnchor) {
    applyAnchor(node, pendingAnchor);
  }
  unfreezeScroll(node);
}

export function hasPendingMessageListScroll() {
  return pendingAnchor != null;
}

export function clearPendingMessageListScroll() {
  const node = document.getElementById("messageListDiv");
  if (node) unfreezeScroll(node);
  pendingAnchor = null;
  guardCleanup?.();
  guardCleanup = null;
}

/**
 * Correct drift from media decode BEFORE paint (ResizeObserver is pre-paint).
 * No requestAnimationFrame — that was the millisecond flicker.
 */
export function startMessageListMediaScrollGuard(
  el?: HTMLElement | null,
  durationMs = 1600,
) {
  const node = el || document.getElementById("messageListDiv");
  if (!node || !pendingAnchor) return;

  guardCleanup?.();
  const myGen = ++guardGeneration;
  const snap = pendingAnchor;
  let userTookOver = false;

  const reapply = () => {
    if (myGen !== guardGeneration || userTookOver || !pendingAnchor) return;
    applyAnchor(node, pendingAnchor);
  };

  const onUserIntent = () => {
    if (myGen !== guardGeneration) return;
    userTookOver = true;
    cleanup();
  };

  const content = (node.firstElementChild as HTMLElement | null) || node;
  const ro = new ResizeObserver(() => {
    // Sync — RO fires after layout, before paint
    reapply();
  });
  ro.observe(content);
  if (content !== node) ro.observe(node);

  node.addEventListener("wheel", onUserIntent, { passive: true });
  node.addEventListener("touchmove", onUserIntent, { passive: true });

  const cleanup = () => {
    if (myGen !== guardGeneration) return;
    window.clearTimeout(timer);
    ro.disconnect();
    node.removeEventListener("wheel", onUserIntent);
    node.removeEventListener("touchmove", onUserIntent);
    pendingAnchor = null;
    if (guardCleanup === cleanup) guardCleanup = null;
  };

  const timer = window.setTimeout(cleanup, durationMs);
  guardCleanup = cleanup;
  pendingAnchor = snap;
}

let deepHistoryGuardCleanup: (() => void) | null = null;

/**
 * While scrolled into mid-history (away from tip), keep the on-screen bubble
 * fixed when late layout grows (link OG, images, fonts). Does not fight the
 * user while they are actively scrolling.
 */
export function startDeepHistoryLayoutGuard(el?: HTMLElement | null) {
  const node = el || document.getElementById("messageListDiv");
  deepHistoryGuardCleanup?.();
  deepHistoryGuardCleanup = null;
  if (!node) return () => {};

  let snap: VisualAnchor | null = readAnchor(node);
  let scrolling = false;
  let scrollIdleTimer: number | null = null;

  const markScrolling = () => {
    scrolling = true;
    if (scrollIdleTimer != null) window.clearTimeout(scrollIdleTimer);
    scrollIdleTimer = window.setTimeout(() => {
      scrolling = false;
      snap = readAnchor(node);
      scrollIdleTimer = null;
    }, 120);
  };

  const onResize = () => {
    if (scrolling) {
      snap = readAnchor(node);
      return;
    }
    if (!snap) {
      snap = readAnchor(node);
      return;
    }
    applyAnchor(node, snap);
    // Refresh offsets after correction (media may still settle)
    const next = readAnchor(node);
    if (next) snap = next;
  };

  const content = (node.firstElementChild as HTMLElement | null) || node;
  const ro = new ResizeObserver(onResize);
  ro.observe(content);
  if (content !== node) ro.observe(node);

  node.addEventListener("scroll", markScrolling, { passive: true });
  node.addEventListener("wheel", markScrolling, { passive: true });
  node.addEventListener("touchmove", markScrolling, { passive: true });

  const cleanup = () => {
    if (scrollIdleTimer != null) window.clearTimeout(scrollIdleTimer);
    ro.disconnect();
    node.removeEventListener("scroll", markScrolling);
    node.removeEventListener("wheel", markScrolling);
    node.removeEventListener("touchmove", markScrolling);
    if (deepHistoryGuardCleanup === cleanup) deepHistoryGuardCleanup = null;
  };
  deepHistoryGuardCleanup = cleanup;
  return cleanup;
}

export function stopDeepHistoryLayoutGuard() {
  deepHistoryGuardCleanup?.();
  deepHistoryGuardCleanup = null;
}
